"use client";

import { useMemo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { SortableList } from "../SortableList";
import { AiAssistButton } from "../AiAssistButton";
import { FormValidityBanner } from "../FormValidityBanner";
import { MobilePreview } from "../MobilePreview";

export interface FillBlankBuilderProps {
  /** Sentence with one or more `_____` placeholders. */
  sentence: string;
  onSentenceChange: (next: string) => void;
  /** Acceptable answers (FIFO, first match wins on the mobile app). */
  variants: string[];
  onVariantsChange: (next: string[]) => void;
  hints: string[];
  onHintsChange: (next: string[]) => void;
  caseSensitive: boolean;
  onCaseSensitiveChange: (next: boolean) => void;
  whitespaceTolerant: boolean;
  onWhitespaceTolerantChange: (next: boolean) => void;
  explanation?: string;
  onExplanationChange?: (next: string) => void;
  level: "A1" | "A2" | "B1" | "B2" | "C1";
}

/**
 * Renders a sentence with `_____` slots highlighted + provides a sortable
 * variants list. The admin types the sentence normally; `_____` (5+ underscores)
 * marks where the learner fills in the blank.
 */
export function FillBlankBuilder({
  sentence,
  onSentenceChange,
  variants,
  onVariantsChange,
  hints,
  onHintsChange,
  caseSensitive,
  onCaseSensitiveChange,
  whitespaceTolerant,
  onWhitespaceTolerantChange,
  explanation,
  onExplanationChange,
  level,
}: FillBlankBuilderProps) {
  const blankCount = useMemo(() => (sentence.match(/_{3,}/g) ?? []).length, [sentence]);

  const validity = useMemo(
    () => [
      {
        label: "Sentence written",
        severity: sentence.trim() ? ("ok" as const) : ("error" as const),
      },
      {
        label: `At least one _____ blank (found ${blankCount})`,
        severity:
          blankCount >= 1
            ? ("ok" as const)
            : ("error" as const),
      },
      {
        label: `At least one acceptable answer (${variants.length})`,
        severity:
          variants.filter((v) => v.trim()).length >= 1
            ? ("ok" as const)
            : ("error" as const),
      },
    ],
    [sentence, blankCount, variants],
  );

  function applyAiVariants(content: unknown) {
    if (!Array.isArray(content)) return;
    const next = [
      ...variants,
      ...content.map((s) => String(s)).filter((s) => !variants.includes(s)),
    ];
    onVariantsChange(next);
  }

  return (
    <div className="flex flex-col gap-4">
      <FormValidityBanner title="Fill in the blank" checks={validity} />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.6fr,1fr]">
        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
              Sentence (use _____ for the blank)
            </label>
            <textarea
              value={sentence}
              onChange={(e) => onSentenceChange(e.target.value)}
              rows={2}
              placeholder="Ich heiße _____."
              className="w-full rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#141414] p-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:border-[#FFD60A] focus:outline-none"
            />
            <p className="mt-1 text-[10px] font-mono text-[#8E8E93]">
              {blankCount === 0
                ? "Tip: type 5+ underscores to mark a blank."
                : `${blankCount} blank${blankCount === 1 ? "" : "s"} detected.`}
            </p>
          </div>
        </div>
        <MobilePreview kind="fill_blank" prompt={sentence} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93]">
            Acceptable answers ({variants.length})
          </label>
          <AiAssistButton
            kind="variants"
            prompt={sentence || "Satz"}
            level={level}
            label="Suggest variants"
            onApply={applyAiVariants}
            disabled={!sentence.trim()}
          />
        </div>
        <SortableList
          items={variants}
          getKey={(_, i) => `variant-${i}`}
          onReorder={onVariantsChange}
          emptyState={
            <div className="text-center py-3">
              <p className="text-[11px] font-mono text-[#8E8E93]">
                Add at least one acceptable spelling. The first matching variant wins.
              </p>
            </div>
          }
          renderRow={({ item: v, index: i, dragHandle }) => (
            <div className="flex items-center gap-2 w-full">
              {dragHandle}
              <input
                type="text"
                value={v}
                onChange={(e) =>
                  onVariantsChange(
                    variants.map((x, j) => (j === i ? e.target.value : x)),
                  )
                }
                placeholder="Acceptable spelling"
                aria-label={`Variant ${i + 1}`}
                className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
              />
              <button
                type="button"
                onClick={() => onVariantsChange(variants.filter((_, j) => j !== i))}
                className="rounded-md p-1.5 text-[#FF453A] hover:bg-[#FF453A]/15"
                aria-label="Remove variant"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        />
        <button
          type="button"
          onClick={() => onVariantsChange([...variants, ""])}
          className="mt-3 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#1C1C1E] border border-dashed border-[rgba(255,255,255,0.12)] text-xs text-[#8E8E93] hover:text-[#FAFAFA] hover:border-[#FFD60A]/40 w-full justify-center"
        >
          <Plus size={14} />
          Add variant
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FgToggle
          label="Case sensitive"
          checked={caseSensitive}
          onChange={onCaseSensitiveChange}
        />
        <FgToggle
          label="Whitespace-tolerant"
          checked={whitespaceTolerant}
          onChange={onWhitespaceTolerantChange}
        />
      </div>

      <div>
        <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
          Hint ladder (max 3 — revealed progressively when learner taps "Hilfe")
        </label>
        <SortableList
          items={hints}
          getKey={(_, i) => `hint-${i}`}
          onReorder={onHintsChange}
          emptyState={
            <p className="text-[11px] font-mono text-[#8E8E93] text-center py-2">
              No hints — learner must answer blind.
            </p>
          }
          renderRow={({ item: h, index: i, dragHandle }) => (
            <div className="flex items-center gap-2 w-full">
              {dragHandle}
              <span className="font-mono text-[10px] text-[#8E8E93] w-6">
                #{i + 1}
              </span>
              <input
                type="text"
                value={h}
                onChange={(e) =>
                  onHintsChange(hints.map((x, j) => (j === i ? e.target.value : x)))
                }
                placeholder="Progressive hint shown after each Hilfe tap"
                aria-label={`Hint ${i + 1}`}
                className="h-9 flex-1 rounded-md border border-[rgba(255,255,255,0.08)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] placeholder:text-[#8E8E93] focus:outline-none focus:border-[#FFD60A]"
              />
              <button
                type="button"
                onClick={() => onHintsChange(hints.filter((_, j) => j !== i))}
                className="rounded-md p-1.5 text-[#FF453A] hover:bg-[#FF453A]/15"
                aria-label="Remove hint"
                disabled={hints.length >= 3}
                title={hints.length >= 3 ? "Max 3 hints" : undefined}
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        />
        {hints.length < 3 && (
          <button
            type="button"
            onClick={() => onHintsChange([...hints, ""])}
            className="mt-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#1C1C1E] border border-dashed border-[rgba(255,255,255,0.12)] text-[11px] text-[#8E8E93] hover:text-[#FAFAFA] hover:border-[#FFD60A]/40"
          >
            <Plus size={12} />
            Add hint
          </button>
        )}
      </div>

      {onExplanationChange && (
        <FgInput
          label="Explanation (optional)"
          value={explanation ?? ""}
          onChange={(e) => onExplanationChange(e.target.value)}
        />
      )}
    </div>
  );
}